import React, { useState, useEffect, Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import { Transition } from '@headlessui/react';

interface NavigationItem {
  title: string;
  url: string;
  order: number;
  isActive: boolean;
  children?: NavigationItem[];
}

interface HeaderSettings {
  backgroundColor?: string;
  textColor?: string;
  accentColor?: string;
  logo?: string;
  title?: string;
  navigationItems?: NavigationItem[];
}

interface SiteSettings {
  footerText?: string;
  copyrightText?: string;
  socialLinks?: {
    facebook?: string;
    linkedin?: string;
    youtube?: string;
  };
}

const BRAND_ACCENT = '#FFB639';
const BRAND_BLACK = '#000000';
const BRAND_WHITE = '#ffffff';

const toBrandColor = (value: string | undefined, fallback: string) => {
  if (!value) return fallback;
  const normalized = value.trim().toUpperCase();
  const known: Record<string, string> = {
    '#FFB639': BRAND_ACCENT,
    '#E5A333': BRAND_ACCENT,
    '#FFD470': BRAND_ACCENT,
    '#000000': BRAND_BLACK,
    'BLACK': BRAND_BLACK,
    '#FFFFFF': BRAND_WHITE,
    'WHITE': BRAND_WHITE,
  };
  if (known[normalized]) return known[normalized];
  return fallback;
};

const ShowcaseLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMobileDropdown, setOpenMobileDropdown] = useState<string | null>(null);
  const location = useLocation();
  const [headerSettings, setHeaderSettings] = useState<HeaderSettings | null>(null);
  const [siteSettings, setSiteSettings] = useState<SiteSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);

  useEffect(() => {
    loadSettings();
  }, [user]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const loadSettings = async () => {
      setSettingsLoading(true);
      try {
        const [headerResponse, siteResponse] = await Promise.all([
          api.get('/site/header'),
          api.get('/site/settings')
        ]);
        setHeaderSettings(headerResponse.data);
        setSiteSettings(siteResponse.data);
      } catch (error) {
        console.error('Error loading settings:', error);
        setHeaderSettings({
          logo: '/logo.png',
          title: 'FORMACADEMY',
          backgroundColor: BRAND_WHITE,
          textColor: BRAND_BLACK,
          accentColor: BRAND_ACCENT,
          navigationItems: []
        });
        setSiteSettings({
          footerText: 'FormAcademy spécialisé...',
          copyrightText: '© 2024 FORMACADEMY. Tous droits réservés.',
          socialLinks: { facebook: '#', linkedin: '#', youtube: '#' }
        });
      } finally {
        setSettingsLoading(false);
      }
    };

  const defaultNavigation = [
    { title: 'Accueil', url: '/', isActive: true, order: 1 },
    { title: 'Catalogue', url: '/formations', isActive: true, order: 2 },
    { title: "L'application", url: '/logiciel', isActive: true, order: 3 },
    { title: 'Les conférences', url: '/conferences', isActive: true, order: 4 },
    { title: 'Le Shop', url: '/shop', isActive: true, order: 5 },
    { title: 'Événements', url: '/evenements', isActive: true, order: 6 },
  ];

  const navigation = (headerSettings?.navigationItems || []).filter(item => item.isActive)
    .sort((a, b) => a.order - b.order);

  const finalNavigation = navigation.length > 0 ? navigation : defaultNavigation;

  const headerStyle = {
    backgroundColor: toBrandColor(headerSettings?.backgroundColor, BRAND_WHITE),
    color: toBrandColor(headerSettings?.textColor, BRAND_BLACK)
  };
  const accentColor = toBrandColor(headerSettings?.accentColor, BRAND_ACCENT);

  const renderNavLink = (item: NavigationItem, isMobile = false) => {
    const isActive = location.pathname === item.url || (item.url !== '/' && location.pathname.startsWith(item.url));
    const linkStyle = {
      color: isActive ? accentColor : headerStyle.color
    };

    if (item.children) {
      return (
        <div className="relative group">
          <span
            className="flex items-center font-medium transition-colors duration-200 hover:text-[#FFB639] cursor-pointer"
            style={linkStyle}
          >
            {item.title}
            <ChevronDown className="ml-1 h-4 w-4 transition-transform duration-200 group-hover:rotate-180" />
          </span>
          <div 
            className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 rounded-lg shadow-lg border border-gray-200 py-2 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 invisible group-hover:visible"
            style={{ backgroundColor: headerStyle.backgroundColor }}
          >
            {item.children.filter(sub => sub.isActive).map(subItem => (
              <Link
                key={subItem.url}
                to={subItem.url}
                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors duration-200"
                style={{ color: headerStyle.color }}
              >
                {subItem.title}
              </Link>
            ))}
          </div>
        </div>
      );
    }

    return (
      <Link
        to={item.url}
        className="font-medium transition-colors duration-200 hover:text-[#FFB639]"
        style={linkStyle}
      >
        {item.title}
      </Link>
    );
  };

  const renderMobileNavLink = (item: NavigationItem) => {
    const isActive = location.pathname === item.url || (item.url !== '/' && location.pathname.startsWith(item.url));
    const linkStyle = {
      color: isActive ? accentColor : headerStyle.color
    };
    const isDropdownOpen = openMobileDropdown === item.url;

    if (item.children) {
      return (
        <div>
          <button
            onClick={() => setOpenMobileDropdown(isDropdownOpen ? null : item.url)}
            className="w-full flex justify-between items-center py-3 text-lg font-semibold"
            style={linkStyle}
          >
            <span>{item.title}</span>
            <ChevronDown className={`h-5 w-5 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          {isDropdownOpen && (
            <div className="pl-4 mt-1 space-y-1 border-l-2" style={{ borderColor: accentColor }}>
              {item.children.filter(sub => sub.isActive).map(subItem => (
                <Link
                  key={subItem.url}
                  to={subItem.url}
                  className="block py-2 text-base font-medium"
                  style={{ color: headerStyle.color }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {subItem.title}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        to={item.url}
        className="block py-3 text-lg font-semibold"
        style={linkStyle}
        onClick={() => setMobileMenuOpen(false)}
      >
        {item.title}
      </Link>
    );
  };

  const renderCTAButton = (isMobile = false) => {
    const commonClasses = `px-6 py-2 rounded-lg font-semibold transition-all duration-300 shadow-lg text-white`;
    const mobileClasses = isMobile ? 'w-full text-center block text-lg py-3' : 'transform hover:scale-105';
    
    const props = {
      className: `${commonClasses} ${mobileClasses}`,
      style: { backgroundColor: accentColor },
      onClick: isMobile ? () => setMobileMenuOpen(false) : undefined
    };

    if (user && user.role) {
      return <Link to={`/${user.role}/dashboard`} {...props}>Mon espace</Link>;
    }
    return <Link to="/login" {...props}>Accès plateforme</Link>;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {settingsLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FFB639]"></div>
        </div>
      )}

      {/* Header */}
      <header className="shadow-md sticky top-0 z-40" style={headerStyle}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex-shrink-0">
              <Link to="/">
                <img 
                  src={headerSettings?.logo || '/logo.png'} 
                  alt={headerSettings?.title || 'FORMACADEMY'} 
                  className="h-14 w-auto"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-10">
              {finalNavigation.map((item) => (
                <React.Fragment key={item.url}>
                  {renderNavLink(item)}
                </React.Fragment>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center">
              {renderCTAButton()}
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="p-2 rounded-md inline-flex items-center justify-center"
                style={{ color: headerStyle.color }}
                aria-label="Ouvrir le menu"
              >
                <Menu className="h-7 w-7" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <Transition
        show={mobileMenuOpen}
        as={Fragment}
        enter="duration-300 ease-out"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="duration-200 ease-in"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/20" onClick={() => setMobileMenuOpen(false)} aria-hidden="true"></div>
          
          <Transition.Child
            as={Fragment}
            enter="duration-300 ease-out transform"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="duration-200 ease-in transform"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <div className="relative flex flex-col w-full max-w-sm ml-auto h-full overflow-y-auto shadow-xl" style={headerStyle}>
              <div className="flex items-center justify-between px-4 h-20 border-b">
                <h2 className="text-xl font-bold">Menu</h2>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 rounded-md"
                  style={{ color: headerStyle.color }}
                  aria-label="Fermer le menu"
                >
                  <X className="h-7 w-7" />
                </button>
              </div>
              <nav className="flex-grow px-4 py-6 space-y-2">
                {finalNavigation.map((item) => (
                  <React.Fragment key={item.url}>
                    {renderMobileNavLink(item)}
                  </React.Fragment>
                ))}
                <div className="border-t border-gray-200/50 my-6"></div>
                {renderCTAButton(true)}
              </nav>
            </div>
          </Transition.Child>
        </div>
      </Transition>

      <main>{children}</main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            <div className="space-y-4">
                <img
                  src={headerSettings?.logo || '/logo.png'}
                  alt={headerSettings?.title || 'FORMACADEMY'}
                  className="h-12 w-auto bg-white p-2 rounded-md"
                />
              <p className="text-gray-400 text-sm">
                {siteSettings?.footerText || 'FormAcademy, la plateforme de formation professionnelle pour développer vos compétences.'}
              </p>
              <div className="flex space-x-4 pt-2">
                {siteSettings?.socialLinks?.facebook && <a href={siteSettings.socialLinks.facebook} className="text-gray-400 hover:text-white"><span className="sr-only">Facebook</span><svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24"><path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/></svg></a>}
                {siteSettings?.socialLinks?.linkedin && <a href={siteSettings.socialLinks.linkedin} className="text-gray-400 hover:text-white"><span className="sr-only">LinkedIn</span><svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg></a>}
                {siteSettings?.socialLinks?.youtube && <a href={siteSettings.socialLinks.youtube} className="text-gray-400 hover:text-white"><span className="sr-only">YouTube</span><svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg></a>}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Formations</h3>
              <ul className="space-y-2 text-sm">
                <li key="footer-formations-gps"><a href="/formations#gps" className="text-gray-400 hover:text-white">Expert gestion GPS</a></li>
                <li key="footer-formations-training"><a href="/formations#training" className="text-gray-400 hover:text-white">Expert charge d'entraînement</a></li>
                <li key="footer-formations-rehabilitation"><a href="/formations#rehabilitation" className="text-gray-400 hover:text-white">Expert réathlétisation</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Plan du site</h3>
              <ul className="space-y-2 text-sm">
                <li key="footer-plan-logiciel"><a href="/logiciel" className="text-gray-400 hover:text-white">L'application</a></li>
                <li key="footer-plan-conferences"><a href="/conferences" className="text-gray-400 hover:text-white">Les conférences</a></li>
                <li key="footer-plan-shop"><a href="/shop" className="text-gray-400 hover:text-white">Le Shop</a></li>
                <li key="footer-plan-evenements"><a href="/evenements" className="text-gray-400 hover:text-white">Événements</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Informations</h3>
              <ul className="space-y-2 text-sm">
                <li key="footer-info-contact"><a href="/contact" className="text-gray-400 hover:text-white">Contact</a></li>
                <li key="footer-info-mentions"><a href="/mentions-legales" className="text-gray-400 hover:text-white">Mentions légales</a></li>
                <li key="footer-info-cgv"><a href="/cgv" className="text-gray-400 hover:text-white">CGV</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-12 pt-8">
            <div className="flex flex-col sm:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm">
                {siteSettings?.copyrightText || '© 2024 FORMACADEMY. Tous droits réservés.'}
              </p>
              <div className="flex items-center space-x-4 mt-4 sm:mt-0">
                <span className="text-gray-400 text-sm">FormAcademy</span>
                <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: accentColor }}>
                  <span className="text-white font-bold text-sm">Q</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ShowcaseLayout;